'use client'
import { type ChangeEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import { InputSelect, type InputSelectProps } from '@/components/InputSelect/InputSelect'
import { getCountryLocale } from '@/utils/l10n/countryUtils'
import { LocaleField } from '@/utils/l10n/locales'
import { getLocaleOrFallback, translateLanguageName } from '@/utils/l10n/localeUtils'
import type { Language } from '@/utils/l10n/types'
import { useChangeLocale } from '@/utils/l10n/useChangeLocale'
import { useCurrentCountry } from '@/utils/l10n/useCurrentCountry'
import { useCurrentLocale } from '@/utils/l10n/useCurrentLocale'

type LanguageSelectorProps = {
  className?: string
  size?: InputSelectProps['size']
  backgroundColor?: InputSelectProps['backgroundColor']
}

export function LanguageSelector({ className, size = 'small', backgroundColor = 'gray300' }: LanguageSelectorProps) {
  const currentCountry = useCurrentCountry()
  const { t } = useTranslation()
  const handleLocaleChange = useChangeLocale()

  const languageList = currentCountry.locales.map((locale) => {
    const { language } = getLocaleOrFallback(locale)
    return {
      name: translateLanguageName(language, t),
      value: language,
    }
  })

  const handleChangeLanguage: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const newLanguage = event.target.value as Language
    const newLocale = getCountryLocale(currentCountry.id, newLanguage)
    handleLocaleChange(newLocale)
  }
  const { language: currentLanguage } = useCurrentLocale()

  return (
    <InputSelect
      name={LocaleField.Language}
      onChange={handleChangeLanguage}
      defaultValue={currentLanguage}
      options={languageList}
      backgroundColor={backgroundColor}
      size={size}
      className={className}
    />
  )
}
